import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter for weight disputes
const weightDisputesAdapter = createEntityAdapter({
    selectId: (weightDispute) => weightDispute._id, // Assuming each dispute has a unique _id field
});

// Define the initial state using the adapter
const initialState = weightDisputesAdapter.getInitialState({
    loading: false,
    error: null,
});

// Async thunk for fetching weight disputes
export const fetchWeightDisputes = createAsyncThunk(
    "weightDispute/fetch",
    async ({ endDate, startDate }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/getWeightDispute", // Endpoint for weight disputes
                method: "GET",
                params: { startDate, endDate }
            });

            console.log("Fetch Weight Disputes Response:", response);

            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response.message || "Failed to fetch weight disputes.");
            }

            return response.data;
        } catch ({ error }) {
            console.error("Error fetching weight disputes:", error.message);
            return rejectWithValue(error.message || "An unexpected error occurred while fetching weight disputes.");
        }
    }
);

// Async thunk for uploading weight disputes
export const uploadWeightDispute = createAsyncThunk(
    "weightDispute/upload",
    async ({ file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("dispute", file);

            const response = await request({
                endpoint: "/admin/uploadWeightDispute",
                method: "POST",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error || "Failed to upload weight dispute");
            }

            return response.data.result; // Return relevant response data
        } catch (error) {
            return rejectWithValue(error.message || "Failed to upload weight dispute");
        }
    }
);

const weightDisputeSlice = createSlice({
    name: "weightDispute",
    initialState,
    reducers: {
        addWeightDisputes: (state, action) => {
            weightDisputesAdapter.setAll(state, action.payload);
        },
        clearWeightDisputes: (state) => {
            weightDisputesAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
        upsertWeightDisputesIO: (state, action) => {
            weightDisputesAdapter.upsertMany(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWeightDisputes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWeightDisputes.fulfilled, (state, action) => {
                state.loading = false;
                weightDisputesAdapter.setAll(state, action.payload);
            })
            .addCase(fetchWeightDisputes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(uploadWeightDispute.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadWeightDispute.fulfilled, (state, action) => {
                state.loading = false;
                weightDisputesAdapter.upsertMany(state, action.payload);
            })
            .addCase(uploadWeightDispute.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Export the generated entity adapter selectors
export const {
    selectAll: selectAllWeightDisputes,
    selectById: selectWeightDisputeById,
    selectIds: selectWeightDisputeIds
} = weightDisputesAdapter.getSelectors((state) => state.weightDispute);

export const { clearWeightDisputes, addWeightDisputes, upsertWeightDisputesIO } = weightDisputeSlice.actions;
export default weightDisputeSlice.reducer;
